export const SITE_LOCALES = [
  { code: 'en', iso: 'en-US', language: 'en-US', name: 'English' },
  { code: 'fr', iso: 'fr-FR', language: 'fr-FR', name: 'Français' },
  { code: 'es', iso: 'es-ES', language: 'es-ES', name: 'Español' },
  { code: 'pt', iso: 'pt-PT', language: 'pt-PT', name: 'Português' },
  { code: 'ja', iso: 'ja-JP', language: 'ja-JP', name: '日本語' },
  { code: 'ko', iso: 'ko-KR', language: 'ko-KR', name: '한국어' },
  { code: 'de', iso: 'de-DE', language: 'de-DE', name: 'Deutsch' },
  { code: 'it', iso: 'it-IT', language: 'it-IT', name: 'Italiano' },
  { code: 'zh', iso: 'zh-CN', language: 'zh-CN', name: '中文' }
] as const

export type SiteLocaleCode = typeof SITE_LOCALES[number]['code']

export const DEFAULT_LOCALE: SiteLocaleCode = 'en'
export const MAGAZINE_LOCALES: SiteLocaleCode[] = ['en', 'fr']
export const NON_DEFAULT_LOCALE_CODES = SITE_LOCALES
  .map(locale => locale.code)
  .filter(code => code !== DEFAULT_LOCALE)

const LOCALE_BY_CODE = Object.fromEntries(
  SITE_LOCALES.map(locale => [locale.code, locale])
) as Record<SiteLocaleCode, typeof SITE_LOCALES[number]>

const LOCALE_PREFIX_RE = new RegExp(`^/(${NON_DEFAULT_LOCALE_CODES.join('|')})(?=/|$)`)

export function isSiteLocale(code: unknown): code is SiteLocaleCode {
  return typeof code === 'string' && code in LOCALE_BY_CODE
}

export function getLocaleFromPath(path: string | undefined | null): SiteLocaleCode {
  if (!path) return DEFAULT_LOCALE
  const pathname = path.split(/[?#]/)[0] ?? '/'
  const match = pathname.match(LOCALE_PREFIX_RE)
  const locale = match?.[1]
  return isSiteLocale(locale) ? locale : DEFAULT_LOCALE
}

export function getLocalePrefix(locale: SiteLocaleCode): string {
  return locale === DEFAULT_LOCALE ? '' : `/${locale}`
}

// French-specific slugs for product routes (EN slug -> FR slug), for SEO.
export const FR_PRODUCT_SLUGS: Record<string, string> = {
  'ankle-boots': 'bottines',
  boots: 'bottes',
  pumps: 'escarpins',
  sandals: 'sandales'
}
const FR_TO_EN_PRODUCT_SLUGS = Object.fromEntries(
  Object.entries(FR_PRODUCT_SLUGS).map(([en, fr]) => [fr, en])
) as Record<string, string>

function mapProductSlug(pathname: string, slugs: Record<string, string>): string {
  const match = pathname.match(/^\/([^/]+)(\/.*)?$/)
  const mapped = match ? slugs[match[1]!] : undefined
  return match && mapped ? `/${mapped}${match[2] || ''}` : pathname
}

export function stripLocalePrefix(path: string | undefined | null): string {
  if (!path) return '/'
  const match = path.match(/^([^?#]*)(.*)$/)
  const pathname = match?.[1] || '/'
  const suffix = match?.[2] || ''
  const localeMatch = pathname.match(LOCALE_PREFIX_RE)

  if (!localeMatch) {
    return `${pathname || '/'}${suffix}`
  }

  const rest = pathname.slice(localeMatch[0].length) || '/'
  const baseRest = localeMatch[1] === 'fr' ? mapProductSlug(rest, FR_TO_EN_PRODUCT_SLUGS) : rest
  return `${baseRest}${suffix}`
}

export function localizePath(path: string, locale: SiteLocaleCode): string {
  const basePath = stripLocalePrefix(path)
  const rawBase = basePath.startsWith('/') ? basePath : `/${basePath}`
  const normalizedBase = locale === 'fr' ? mapProductSlug(rawBase, FR_PRODUCT_SLUGS) : rawBase
  const prefix = getLocalePrefix(locale)
  return `${prefix}${normalizedBase === '/' ? '' : normalizedBase}` || '/'
}

export function isMagazinePath(path: string | undefined | null): boolean {
  const basePath = stripLocalePrefix(path).split(/[?#]/)[0] ?? '/'
  return basePath === '/magazine' || basePath.startsWith('/magazine/')
}

export function getLocaleLanguage(locale: SiteLocaleCode | string): string {
  return isSiteLocale(locale) ? LOCALE_BY_CODE[locale].language : LOCALE_BY_CODE[DEFAULT_LOCALE].language
}

export function getLocaleIso(locale: SiteLocaleCode | string): string {
  return isSiteLocale(locale) ? LOCALE_BY_CODE[locale].iso : LOCALE_BY_CODE[DEFAULT_LOCALE].iso
}

export function getOgLocale(locale: SiteLocaleCode | string): string {
  return getLocaleIso(locale).replace('-', '_')
}

export function getLocaleName(locale: SiteLocaleCode | string): string {
  return isSiteLocale(locale) ? LOCALE_BY_CODE[locale].name : LOCALE_BY_CODE[DEFAULT_LOCALE].name
}
